import { AlertTriangle, Loader2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
// the store module only: the feature barrel also carries the flow's dialogs,
// and the sign-out one imports the logout routine.
import { useFlowStore } from '@/features/flow/store';
import { completeOAuthCallback } from '@/lib/api/auth';
import type { MeResponse } from '@/lib/api/auth';

import { useOAuthSession } from '../hooks/useOAuthSession';
import {
    shouldRequestOAuthBinding,
    useOAuthBindingDialogStore,
} from '../oauthBindingDialogStore';
import { resolveUnknownOAuthBinding } from '../oauthBindingResolution';
import { useOAuthStore } from '../store';

/**
 * Handles the OAuth redirect back from the provider (`/auth/callback?code&state`).
 * Completes the handshake with `POST /v2/auth/callback` (which sets the session
 * cookie), seeds the session, then returns to the app. With the cookie in place
 * the speed-trading deposit can auto-provision the user's custodial wallet.
 */
const OAuthCallbackPage: React.FC = () => {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const setMe = useOAuthStore((s) => s.setMe);
    const requestBinding = useOAuthBindingDialogStore((s) => s.request);
    const { refresh, rebind } = useOAuthSession();
    const [error, setError] = useState<string | null>(null);
    const ranRef = useRef(false);

    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;

        const code = params.get('code');
        const state = params.get('state');
        const providerError = params.get('error');

        if (providerError) {
            setError(`Sign-in was cancelled or failed (${providerError}).`);
            return;
        }
        if (!code || !state) {
            setError(
                'Missing authorization code, please try signing in again.',
            );
            return;
        }

        void (async () => {
            try {
                const result = await completeOAuthCallback(code, state);
                const seeded: MeResponse = {
                    authenticated: true,
                    provider: result.provider,
                    accountHash: result.accountHash,
                    bindingState: result.bindingState,
                    displayName: result.displayName,
                    username: result.username,
                };
                setMe(seeded);
                await refresh();
                let bindingState = result.bindingState;
                if (result.bindingState === 'unknown') {
                    bindingState = await resolveUnknownOAuthBinding(rebind);
                }
                // which flow (V1 custodial / V2 browser wallet) this login is in, from the fullest
                // profile at hand: the refreshed one carries the custodial and anchor wallets the
                // callback answer does not. The V1 binding request below waits for it.
                const refreshed = useOAuthStore.getState().me;
                const flow = await useFlowStore.getState().resolve({
                    ...(refreshed?.authenticated &&
                    refreshed.accountHash === result.accountHash
                        ? refreshed
                        : seeded),
                    bindingState,
                });
                if (
                    flow === 'v1' &&
                    shouldRequestOAuthBinding({
                        provider: result.provider,
                        accountHash: result.accountHash,
                        bindingState,
                    })
                ) {
                    requestBinding({
                        provider: result.provider,
                        accountHash: result.accountHash,
                    });
                }
                navigate('/', { replace: true });
            } catch (caught) {
                setError(
                    caught instanceof Error
                        ? caught.message
                        : 'Sign-in failed. Please try again.',
                );
            }
        })();
    }, [params, navigate, refresh, rebind, requestBinding, setMe]);

    return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
            {error ? (
                <>
                    <AlertTriangle size={28} className="text-destructive" />
                    <p
                        role="alert"
                        className="text-sm font-semibold text-foreground"
                    >
                        Sign-in failed
                    </p>
                    <p className="max-w-[320px] text-xs text-muted-foreground">
                        {error}
                    </p>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void navigate('/', { replace: true })}
                    >
                        Back to app
                    </Button>
                </>
            ) : (
                <>
                    <Loader2
                        size={28}
                        className="animate-spin text-muted-foreground"
                    />
                    <p
                        role="status"
                        className="text-sm font-medium text-muted-foreground"
                    >
                        Completing sign-in…
                    </p>
                </>
            )}
        </div>
    );
};

export default OAuthCallbackPage;
